import { create } from "zustand";
import { immer } from "zustand/middleware/immer";
import { devtools } from "zustand/middleware";
import { ShippingService } from "../services/shipping.service";
import type {
  TaxSettings,
  SpecificTaxRate,
  ResourceRef,
} from "@codway-dev/shared";

// ---------- Defaults ----------

const DEFAULT_TAX_SETTINGS: TaxSettings = {
  enabled: false,
  apply_to: "products_only",
  rate: 0,
  name: "",
  included_in_price: false,
  country_codes: [],
  specific_rates: [],
};

// ---------- Types ----------

interface TaxEditorState {
  settings: TaxSettings;
  initialSettings: TaxSettings | null;
  currency: string;
  isLoading: boolean;
  isSaving: boolean;
  error: string | null;
}

interface TaxEditorActions {
  loadFromBackend: () => Promise<void>;
  updateSetting: <K extends keyof TaxSettings>(
    key: K,
    value: TaxSettings[K]
  ) => void;
  addSpecificRate: () => void;
  updateSpecificRate: (id: string, updates: Partial<SpecificTaxRate>) => void;
  removeSpecificRate: (id: string) => void;
  addProductsToRate: (rateId: string, products: ResourceRef[]) => void;
  removeProductFromRate: (rateId: string, productId: string) => void;
  addCollectionsToRate: (rateId: string, collections: ResourceRef[]) => void;
  removeCollectionFromRate: (rateId: string, collectionId: string) => void;
  addCountryCode: (code: string) => void;
  removeCountryCode: (code: string) => void;
  save: () => Promise<void>;
  discardChanges: () => void;
}

type TaxEditorStore = TaxEditorState & TaxEditorActions;

// ---------- Selector ----------

export const selectTaxIsDirty = (state: TaxEditorStore) => {
  if (!state.initialSettings) return false;
  return (
    JSON.stringify(state.settings) !== JSON.stringify(state.initialSettings)
  );
};

// ---------- Store ----------

export const useTaxEditorStore = create<TaxEditorStore>()(
  devtools(
    immer((set, get) => ({
      settings: DEFAULT_TAX_SETTINGS,
      initialSettings: null,
      currency: "USD",
      isLoading: false,
      isSaving: false,
      error: null,

      loadFromBackend: async () => {
        set((state) => {
          state.isLoading = true;
          state.error = null;
        });
        try {
          const data = await ShippingService.getLogistics();
          const tax = data.tax ?? DEFAULT_TAX_SETTINGS;
          set((state) => {
            state.settings = tax;
            state.initialSettings = JSON.parse(JSON.stringify(tax));
            state.currency = data.currency;
            state.isLoading = false;
          });
        } catch (err) {
          set((state) => {
            state.error =
              err instanceof Error ? err.message : "Failed to load settings";
            state.isLoading = false;
          });
        }
      },

      updateSetting: (key, value) =>
        set((state) => {
          (state.settings as Record<string, unknown>)[key] = value;
          if (key === "enabled" && value === true) {
            state.settings.country_codes = [];
          }
        }),

      addSpecificRate: () =>
        set((state) => {
          const newId = `tax_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`;
          state.settings.specific_rates.push({
            id: newId,
            name: "",
            rate: 0,
            products: [],
            collections: [],
          });
        }),

      updateSpecificRate: (id, updates) =>
        set((state) => {
          const index = state.settings.specific_rates.findIndex(
            (r) => r.id === id
          );
          if (index === -1) return;
          Object.assign(state.settings.specific_rates[index], updates);
        }),

      removeSpecificRate: (id) =>
        set((state) => {
          state.settings.specific_rates = state.settings.specific_rates.filter(
            (r) => r.id !== id
          );
        }),

      addProductsToRate: (rateId, products) =>
        set((state) => {
          const rate = state.settings.specific_rates.find(
            (r) => r.id === rateId
          );
          if (!rate) return;
          const existingIds = new Set(rate.products.map((p) => p.id));
          const newProducts = products.filter((p) => !existingIds.has(p.id));
          rate.products.push(...newProducts);
        }),

      removeProductFromRate: (rateId, productId) =>
        set((state) => {
          const rate = state.settings.specific_rates.find(
            (r) => r.id === rateId
          );
          if (!rate) return;
          rate.products = rate.products.filter((p) => p.id !== productId);
        }),

      addCollectionsToRate: (rateId, collections) =>
        set((state) => {
          const rate = state.settings.specific_rates.find(
            (r) => r.id === rateId
          );
          if (!rate) return;
          const existingIds = new Set(rate.collections.map((c) => c.id));
          const newCollections = collections.filter(
            (c) => !existingIds.has(c.id)
          );
          rate.collections.push(...newCollections);
        }),

      removeCollectionFromRate: (rateId, collectionId) =>
        set((state) => {
          const rate = state.settings.specific_rates.find(
            (r) => r.id === rateId
          );
          if (!rate) return;
          rate.collections = rate.collections.filter(
            (c) => c.id !== collectionId
          );
        }),

      addCountryCode: (code) =>
        set((state) => {
          if (!state.settings.country_codes.includes(code)) {
            state.settings.country_codes.push(code);
          }
        }),

      removeCountryCode: (code) =>
        set((state) => {
          state.settings.country_codes = state.settings.country_codes.filter(
            (c) => c !== code
          );
        }),

      save: async () => {
        set((state) => {
          state.isSaving = true;
        });
        try {
          const { settings } = get();
          await ShippingService.updateLogistics({ tax: settings });
          set((state) => {
            state.isSaving = false;
            state.initialSettings = JSON.parse(JSON.stringify(state.settings));
          });
        } catch (err) {
          set((state) => {
            state.isSaving = false;
          });
          throw err instanceof Error ? err : new Error("Failed to save");
        }
      },

      discardChanges: () =>
        set((state) => {
          if (state.initialSettings) {
            state.settings = JSON.parse(JSON.stringify(state.initialSettings));
          }
        }),
    })),
    { name: "TaxEditorStore" }
  )
);
